import { clearAdminProxy } from "@/api";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/auth/useAuth";
import { useOrganization } from "@/contexts/frontend/organization/useOrganization";
import { useProcessingScopes } from "@/contexts/frontend/processing-scopes/useProcessingScopes";
import { useIsMobile } from "@/hooks/use-mobile";
import { removeOrganizationQueries } from "@/hooks/useOrganizationProxy";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import { enqueueSnackbar } from "notistack";
import { useNavigate } from "react-router";

export const AdminBanner = () => {
    const { user } = useAuth();
    const isMobile = useIsMobile();
    const { organization } = useOrganization();
    const { clearProcessingScopes } = useProcessingScopes();
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const { mutate: clearProxy, isPending } = useMutation({
        mutationFn: clearAdminProxy,
        onSuccess: () => {
            queryClient.removeQueries({ queryKey: ["getAdminProxy"] });
            removeOrganizationQueries(queryClient);
            clearProcessingScopes();
            navigate("/admin");
        },
        onError: () => {
            enqueueSnackbar("Failed to return to admin dashboard", {
                variant: "error",
            });
        },
    });

    if (!user || !user.is_admin) return null;

    return (
        <div className="sticky top-(--header-height) z-40">
            <div
                role="status"
                className="bg-primary text-primary-foreground flex h-(--admin-bar-height) w-full items-center justify-between gap-4 px-4 py-2.5"
            >
                <div className="flex min-w-0 items-center gap-2">
                    <ShieldCheck className="size-5 shrink-0" />
                    <div className="flex min-w-0 items-center gap-1">
                        <span className="truncate text-sm font-semibold">
                            Viewing {organization.name} as Administrator
                        </span>
                        {!isMobile && (
                            <span className="shrink-0 text-xs opacity-90">
                                • Changes made here will affect this
                                organization
                            </span>
                        )}
                    </div>
                </div>
                <Button
                    variant={"ghost"}
                    size="sm"
                    onClick={() => clearProxy()}
                    disabled={isPending}
                >
                    {!isMobile && <ArrowLeft className="mr-1 size-4" />}
                    {isMobile ? "Admin" : "Return to Admin"}
                </Button>
            </div>
        </div>
    );
};
